import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../lib/auth';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import { 
  Plus, Edit, Trash2, Loader2, Zap, Clock, AlertTriangle,
  AlertCircle as AlertIcon, Link2, Unlink, Play, GitBranch,
  FileText, Mail, Calendar, CheckCircle, Users, Settings
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Trigger type icons and colors
const triggerConfig = {
  manual: { label: 'Manual', icon: Play, color: 'bg-slate-100 text-slate-700' },
  case_creation: { label: 'Case Creation', icon: Plus, color: 'bg-green-100 text-green-700' },
  case_stage_change: { label: 'Stage Change', icon: GitBranch, color: 'bg-blue-100 text-blue-700' },
  document_upload: { label: 'Document Upload', icon: FileText, color: 'bg-purple-100 text-purple-700' },
  hearing_notice_scan: { label: 'Hearing Notice', icon: Calendar, color: 'bg-orange-100 text-orange-700' },
  workflow_ai_automation: { label: 'Workflow.AI', icon: Zap, color: 'bg-indigo-100 text-indigo-700' },
  intake_completed: { label: 'Intake Complete', icon: CheckCircle, color: 'bg-emerald-100 text-emerald-700' },
  document_signed: { label: 'Doc Signed', icon: FileText, color: 'bg-cyan-100 text-cyan-700' },
  calendar_event: { label: 'Calendar Event', icon: Calendar, color: 'bg-amber-100 text-amber-700' },
  document_template: { label: 'Doc Template', icon: FileText, color: 'bg-pink-100 text-pink-700' },
  deadline_approaching: { label: 'Deadline', icon: Clock, color: 'bg-red-100 text-red-700' }
};

// Case types
const CASE_TYPES = [
  { id: 'personal_injury', name: 'Personal Injury' },
  { id: 'family_law', name: 'Family Law' },
  { id: 'employment', name: 'Employment' },
  { id: 'business_litigation', name: 'Business Litigation' },
  { id: 'real_estate', name: 'Real Estate' },
  { id: 'criminal', name: 'Criminal Defense' },
  { id: 'estate_planning', name: 'Estate Planning' },
  { id: 'bankruptcy', name: 'Bankruptcy' },
  { id: 'immigration', name: 'Immigration' },
  { id: 'intellectual_property', name: 'Intellectual Property' }
];

export default function WorkflowAutomationsManager() {
  const { token, user } = useAuth();
  
  // State
  const [workflows, setWorkflows] = useState([]);
  const [workflowAIAutomations, setWorkflowAIAutomations] = useState([]);
  const [automationLinks, setAutomationLinks] = useState([]);
  const [taskTemplates, setTaskTemplates] = useState({ system_templates: [], custom_templates: [] });
  const [loading, setLoading] = useState(true);
  const [triggerTypes, setTriggerTypes] = useState({});
  
  // Dialog state
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [editingWorkflow, setEditingWorkflow] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [form, setForm] = useState({
    name: '',
    description: '',
    trigger_type: 'case_creation',
    trigger_config: {},
    case_types: [],
    tasks: [],
    is_active: true
  });
  
  // Link form state
  const [selectedAutomation, setSelectedAutomation] = useState(null);
  const [linkWorkflowId, setLinkWorkflowId] = useState('');
  
  // Task in form
  const [currentTask, setCurrentTask] = useState({
    title: '',
    description: '',
    task_type: 'other',
    priority: 'medium',
    deadline_days: 7,
    deadline_direction: 'after',
    deadline_calculation: 'fixed_days',
    assignee_role: 'none'
  });

  // Check if user is manager
  const isManager = ['owner', 'manager', 'admin'].includes(user?.role) || !user?.firm_id || user?.firm_id === user?.id;

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      const [workflowsRes, automationsRes, linksRes, templatesRes] = await Promise.all([
        fetch(`${API_URL}/api/case-management/task-workflows`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/case-management/workflow-ai-automations`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/case-management/workflow-automation-links`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/case-management/task-templates`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      if (workflowsRes.ok) {
        const data = await workflowsRes.json();
        setWorkflows(data.workflows || []);
        setTriggerTypes(data.trigger_types || {});
      }
      
      if (automationsRes.ok) {
        const data = await automationsRes.json();
        setWorkflowAIAutomations(data.automations || []);
      }
      
      if (linksRes.ok) {
        const data = await linksRes.json();
        setAutomationLinks(data.links || []);
      }
      
      if (templatesRes.ok) {
        setTaskTemplates(await templatesRes.json());
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Reset form
  const resetForm = () => {
    setForm({
      name: '',
      description: '',
      trigger_type: 'case_creation',
      trigger_config: {},
      case_types: [],
      tasks: [],
      is_active: true
    });
    setCurrentTask({
      title: '',
      description: '',
      task_type: 'other',
      priority: 'medium',
      deadline_days: 7,
      deadline_direction: 'after',
      deadline_calculation: 'fixed_days',
      assignee_role: 'none'
    });
    setEditingWorkflow(null);
  };

  // Handle create
  const handleCreate = () => {
    resetForm();
    setShowCreateDialog(true);
  };

  // Handle edit
  const handleEdit = (workflow) => {
    setEditingWorkflow(workflow);
    setForm({
      name: workflow.name,
      description: workflow.description || '',
      trigger_type: workflow.trigger_type,
      trigger_config: workflow.trigger_config || {},
      case_types: workflow.case_types || [],
      tasks: workflow.tasks || [],
      is_active: workflow.is_active
    });
    setShowCreateDialog(true);
  };

  // Add task to form
  const addTask = () => {
    if (!currentTask.title) {
      toast.error('Please enter a task title');
      return;
    }
    setForm(prev => ({
      ...prev,
      tasks: [...prev.tasks, { ...currentTask }]
    }));
    setCurrentTask({
      title: '',
      description: '',
      task_type: 'other',
      priority: 'medium',
      deadline_days: 7,
      deadline_direction: 'after',
      deadline_calculation: 'fixed_days',
      assignee_role: 'none'
    });
  };

  // Remove task from form
  const removeTask = (index) => {
    setForm(prev => ({
      ...prev,
      tasks: prev.tasks.filter((_, i) => i !== index)
    }));
  };

  // Toggle case type
  const toggleCaseType = (typeId) => {
    setForm(prev => ({
      ...prev,
      case_types: prev.case_types.includes(typeId)
        ? prev.case_types.filter(t => t !== typeId)
        : [...prev.case_types, typeId]
    }));
  };

  // Save workflow
  const handleSave = async () => {
    if (!form.name) {
      toast.error('Please enter a workflow name');
      return;
    }
    if (form.tasks.length === 0) {
      toast.error('Please add at least one task');
      return;
    }

    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('name', form.name);
      formData.append('description', form.description);
      formData.append('trigger_type', form.trigger_type);
      formData.append('trigger_config', JSON.stringify(form.trigger_config));
      formData.append('case_types', JSON.stringify(form.case_types));
      formData.append('tasks', JSON.stringify(form.tasks));
      formData.append('is_active', form.is_active);

      const url = editingWorkflow 
        ? `${API_URL}/api/case-management/task-workflows/${editingWorkflow.id}`
        : `${API_URL}/api/case-management/task-workflows`;
      
      const res = await fetch(url, {
        method: editingWorkflow ? 'PUT' : 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (res.ok) {
        toast.success(editingWorkflow ? 'Workflow updated' : 'Workflow created');
        setShowCreateDialog(false);
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to save workflow');
      }
    } catch (error) {
      toast.error('Failed to save workflow');
    } finally {
      setSaving(false);
    }
  };

  // Delete workflow
  const handleDelete = async (workflowId) => {
    if (!confirm('Are you sure you want to delete this workflow?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/case-management/task-workflows/${workflowId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Workflow deleted');
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to delete workflow');
      }
    } catch (error) {
      toast.error('Failed to delete workflow');
    }
  };

  // Create workflow from Workflow.AI automation
  const handleCreateFromAutomation = async (automation) => {
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('automation_id', automation.id);
      formData.append('workflow_ai_id', automation.workflow_id);
      formData.append('name', `Auto: ${automation.title}`);
      formData.append('case_types', JSON.stringify([]));

      const res = await fetch(`${API_URL}/api/case-management/create-workflow-from-automation`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (res.ok) {
        toast.success(`Task workflow created from "${automation.title}"`);
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to create workflow');
      }
    } catch (error) {
      toast.error('Failed to create workflow');
    } finally {
      setSaving(false);
    }
  };

  // Delete automation link
  const handleDeleteLink = async (linkId) => {
    if (!confirm('Are you sure you want to unlink this automation?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/case-management/workflow-automation-links/${linkId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Automation unlinked');
        fetchData();
      } else {
        toast.error('Failed to unlink automation');
      }
    } catch (error) {
      toast.error('Failed to unlink automation');
    }
  };

  // Get trigger icon
  const getTriggerIcon = (type) => {
    const config = triggerConfig[type];
    if (!config) return Play;
    return config.icon;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="workflow-automations-manager">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Workflow Automations</h2>
          <p className="text-sm text-slate-500">
            {workflows.length} workflow(s) • {automationLinks.length} linked automation(s)
          </p>
        </div>
        {isManager && (
          <Button onClick={handleCreate} data-testid="create-workflow-btn">
            <Plus className="w-4 h-4 mr-1" />
            New Workflow
          </Button>
        )}
      </div>

      {/* Workflow.AI Automations Section */}
      {workflowAIAutomations.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Zap className="w-4 h-4 text-indigo-600" />
              Workflow.AI Automations
            </CardTitle>
            <CardDescription>
              Create task workflows from your Workflow.AI automation points
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {workflowAIAutomations.map(automation => {
                const isLinked = automationLinks.some(l => l.automation_id === automation.id);
                return (
                  <div 
                    key={automation.id}
                    className={`p-3 border rounded-lg ${isLinked ? 'bg-indigo-50 border-indigo-200' : 'bg-white hover:bg-slate-50'}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm">{automation.title}</span>
                          {isLinked && (
                            <Badge variant="outline" className="text-[10px] bg-indigo-100 text-indigo-700">
                              <Link2 className="w-2 h-2 mr-0.5" />
                              Linked
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-slate-500 mt-1 line-clamp-2">{automation.description}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge variant="outline" className="text-[10px]">
                            {automation.workflow_name}
                          </Badge>
                          {automation.impact && (
                            <Badge 
                              variant="outline" 
                              className={`text-[10px] ${
                                automation.impact === 'high' ? 'bg-green-100 text-green-700' :
                                automation.impact === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-slate-100 text-slate-700'
                              }`}
                            >
                              {automation.impact} impact
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    {isManager && !isLinked && (
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="mt-2 w-full"
                        onClick={() => handleCreateFromAutomation(automation)}
                        disabled={saving}
                      >
                        <Zap className="w-3 h-3 mr-1" />
                        Create Task Workflow
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Active Workflows */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <GitBranch className="w-4 h-4 text-blue-600" />
            Task Workflows
          </CardTitle>
          <CardDescription>
            Automated task creation when events occur
          </CardDescription>
        </CardHeader>
        <CardContent>
          {workflows.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <GitBranch className="w-12 h-12 mx-auto text-slate-300 mb-2" />
              <p>No workflows created yet</p>
              <p className="text-sm">Create a workflow to automatically generate tasks</p>
            </div>
          ) : (
            <div className="space-y-3">
              {workflows.map(workflow => {
                const TriggerIcon = getTriggerIcon(workflow.trigger_type);
                const triggerConf = triggerConfig[workflow.trigger_type] || { label: workflow.trigger_type, color: 'bg-slate-100 text-slate-700' };
                
                return (
                  <div 
                    key={workflow.id}
                    className={`p-4 border rounded-lg ${workflow.is_active ? 'bg-white' : 'bg-slate-50 opacity-60'}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{workflow.name}</span>
                          {!workflow.is_active && (
                            <Badge variant="outline" className="text-[10px]">Disabled</Badge>
                          )}
                          {workflow.source === 'workflow_ai' && (
                            <Badge variant="outline" className="text-[10px] bg-indigo-50 text-indigo-700">
                              <Zap className="w-2 h-2 mr-0.5" />
                              Workflow.AI
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-500 mt-1">{workflow.description}</p>
                        
                        <div className="flex items-center gap-3 mt-3">
                          <Badge variant="outline" className={`flex items-center gap-1 ${triggerConf.color}`}>
                            <TriggerIcon className="w-3 h-3" />
                            {triggerConf.label}
                          </Badge>
                          <span className="text-xs text-slate-500">
                            {workflow.tasks?.length || 0} task(s)
                          </span>
                          <span className="text-xs text-slate-500">
                            Triggered {workflow.times_triggered || 0} times
                          </span>
                        </div>
                        
                        {workflow.case_types?.length > 0 && (
                          <div className="flex items-center gap-1 mt-2 flex-wrap">
                            <span className="text-xs text-slate-400">Case types:</span>
                            {workflow.case_types.map(t => (
                              <Badge key={t} variant="outline" className="text-[10px]">
                                {t.replace('_', ' ')}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      {isManager && (
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(workflow)}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleDelete(workflow.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GitBranch className="w-5 h-5 text-blue-600" />
              {editingWorkflow ? 'Edit Workflow' : 'New Task Workflow'}
            </DialogTitle>
            <DialogDescription>
              Define when tasks should be automatically created
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label htmlFor="name">Workflow Name *</Label>
                <Input
                  id="name"
                  value={form.name}
                  onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., New Case Setup Tasks"
                />
              </div>
              <div className="col-span-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={form.description}
                  onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe when this workflow runs..."
                  rows={2}
                />
              </div>
            </div>

            {/* Trigger Type */}
            <div>
              <Label>Trigger Type</Label>
              <Select
                value={form.trigger_type}
                onValueChange={(v) => setForm(prev => ({ ...prev, trigger_type: v, trigger_config: {} }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(triggerTypes).map(([key, config]) => (
                    <SelectItem key={key} value={key}>
                      <div className="flex items-center gap-2">
                        {(() => {
                          const TIcon = triggerConfig[key]?.icon || Play;
                          return <TIcon className="w-3 h-3" />;
                        })()}
                        {config.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">
                {triggerTypes[form.trigger_type]?.description}
              </p>
            </div>

            {/* Case Types */}
            <div>
              <Label>Applicable Case Types (leave empty for all)</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2 p-3 border rounded-lg max-h-32 overflow-y-auto">
                {CASE_TYPES.map(type => (
                  <div key={type.id} className="flex items-center gap-2">
                    <Checkbox
                      id={`wf-case-type-${type.id}`}
                      checked={form.case_types.includes(type.id)}
                      onCheckedChange={() => toggleCaseType(type.id)}
                    />
                    <Label htmlFor={`wf-case-type-${type.id}`} className="cursor-pointer text-sm">
                      {type.name}
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            {/* Tasks */}
            <div>
              <Label className="flex items-center justify-between">
                <span>Tasks to Create</span>
                <span className="text-xs text-slate-400">{form.tasks.length} task(s)</span>
              </Label>
              
              {/* Existing tasks */}
              {form.tasks.length > 0 && (
                <div className="space-y-2 mt-2 mb-3">
                  {form.tasks.map((task, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-2 bg-slate-50 rounded border">
                      <div className="flex-1">
                        <span className="font-medium text-sm">{task.title}</span>
                        <span className="text-xs text-slate-500 ml-2">
                          {task.deadline_days}d {task.deadline_direction} • {task.priority}
                        </span>
                      </div>
                      <Button 
                        type="button"
                        variant="ghost" 
                        size="sm"
                        onClick={() => removeTask(idx)}
                        className="h-6 w-6 p-0 text-red-500"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add new task */}
              <div className="p-3 border rounded-lg bg-slate-50 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <Input
                      value={currentTask.title}
                      onChange={(e) => setCurrentTask(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="Task title..."
                    />
                  </div>
                  <Select
                    value={currentTask.priority}
                    onValueChange={(v) => setCurrentTask(prev => ({ ...prev, priority: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Priority" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="emergency">Emergency</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select
                    value={currentTask.assignee_role}
                    onValueChange={(v) => setCurrentTask(prev => ({ ...prev, assignee_role: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Assign to role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No default role</SelectItem>
                      <SelectItem value="attorney">Attorney</SelectItem>
                      <SelectItem value="paralegal">Paralegal</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="partner">Partner</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min="1"
                      value={currentTask.deadline_days}
                      onChange={(e) => setCurrentTask(prev => ({ ...prev, deadline_days: parseInt(e.target.value) || 7 }))}
                      className="w-20"
                    />
                    <span className="text-sm text-slate-500">days</span>
                  </div>
                  <Select
                    value={currentTask.deadline_direction}
                    onValueChange={(v) => setCurrentTask(prev => ({ ...prev, deadline_direction: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="after">After trigger</SelectItem>
                      <SelectItem value="before">Before trigger</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select
                    value={currentTask.deadline_calculation}
                    onValueChange={(v) => setCurrentTask(prev => ({ ...prev, deadline_calculation: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fixed_days">Calendar days</SelectItem>
                      <SelectItem value="business_days">Business days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button type="button" variant="outline" onClick={addTask} className="w-full">
                  <Plus className="w-4 h-4 mr-1" />
                  Add Task
                </Button>
              </div>
            </div>

            {/* Active toggle */}
            <div className="flex items-center gap-2">
              <Checkbox
                id="is_active"
                checked={form.is_active}
                onCheckedChange={(checked) => setForm(prev => ({ ...prev, is_active: checked }))}
              />
              <Label htmlFor="is_active" className="cursor-pointer">
                Workflow is active
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  Saving...
                </>
              ) : (
                editingWorkflow ? 'Update Workflow' : 'Create Workflow'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
